import { db } from '../firebase'
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore'

/**
 * Email Template Service - Firestore Integration
 * Manages email templates in Firestore database
 */

class EmailTemplateService {
  constructor() {
    this.collectionName = 'email_templates'
  }

  getTenantTemplatesRef(tenantId) {
    return collection(db, 'MarketGenie_tenants', tenantId, this.collectionName)
  }

  getLegacyTemplatesRef() {
    return collection(db, this.collectionName)
  }

  /**
   * Get all templates for a user
   */
  async getTemplates(tenantId) {
    try {
      const templatesRef = this.getTenantTemplatesRef(tenantId)
      const q = query(
        templatesRef,
        orderBy('createdAt', 'desc')
      )
      
      const querySnapshot = await getDocs(q)
      const templates = []
      
      querySnapshot.forEach((doc) => {
        templates.push({
          id: doc.id,
          ...doc.data()
        })
      })

      if (templates.length > 0) {
        return templates
      }

      // Legacy fallback: if tenant-specific templates are missing, try root templates only if allowed
      try {
        const legacyRef = this.getLegacyTemplatesRef()
        const legacyQuery = query(
          legacyRef,
          where('tenantId', '==', tenantId),
          orderBy('createdAt', 'desc')
        )
        const legacySnapshot = await getDocs(legacyQuery)
        const legacyTemplates = []
        legacySnapshot.forEach((doc) => {
          legacyTemplates.push({
            id: doc.id,
            ...doc.data()
          })
        })

        return legacyTemplates
      } catch (legacyError) {
        if (legacyError.code === 'permission-denied') {
          console.warn('Legacy email template fallback blocked by security rules. Returning empty template list.')
          return []
        }
        throw legacyError
      }
    } catch (error) {
      console.error('Error fetching templates:', error)
      throw error
    }
  }

  /**
   * Get a single template by ID
   */
  async getTemplate(templateId, tenantId = null) {
    try {
      let templateRef
      if (tenantId) {
        templateRef = doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, templateId)
      } else {
        templateRef = doc(db, this.collectionName, templateId)
      }
      const templateSnap = await getDoc(templateRef)
      
      if (templateSnap.exists()) {
        return {
          id: templateSnap.id,
          ...templateSnap.data()
        }
      }

      if (tenantId) {
        const legacyRef = doc(db, this.collectionName, templateId)
        const legacySnap = await getDoc(legacyRef)
        if (legacySnap.exists()) {
          return {
            id: legacySnap.id,
            ...legacySnap.data()
          }
        }
      }

      throw new Error('Template not found')
    } catch (error) {
      console.error('Error fetching template:', error)
      throw error
    }
  }

  /**
   * Create a new template
   */
  async createTemplate(tenantId, templateData) {
    try {
      const templatesRef = this.getTenantTemplatesRef(tenantId)
      
      const newTemplate = {
        ...templateData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usageCount: 0
      }
      
      const docRef = await addDoc(templatesRef, newTemplate)
      
      return {
        id: docRef.id,
        ...newTemplate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error creating template:', error)
      throw error
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(templateId, templateData, tenantId = null) {
    try {
      const templateRef = tenantId
        ? doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, templateId)
        : doc(db, this.collectionName, templateId)
      
      const updateData = {
        ...templateData,
        updatedAt: serverTimestamp()
      }
      
      // Remove fields that shouldn't be updated
      delete updateData.id
      delete updateData.tenantId
      delete updateData.createdAt
      
      await updateDoc(templateRef, updateData)
      
      return {
        id: templateId,
        ...templateData,
        updatedAt: new Date().toISOString()
      }
    } catch (error) {
      console.error('Error updating template:', error)
      throw error
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId, tenantId = null) {
    try {
      const templateRef = tenantId
        ? doc(db, 'MarketGenie_tenants', tenantId, this.collectionName, templateId)
        : doc(db, this.collectionName, templateId)
      await deleteDoc(templateRef)
      return true
    } catch (error) {
      console.error('Error deleting template:', error)
      throw error
    }
  }

  /**
   * Increment usage count for a template
   */
  async incrementUsageCount(templateId) {
    try {
      const templateRef = doc(db, this.collectionName, templateId)
      const templateSnap = await getDoc(templateRef)
      
      if (templateSnap.exists()) {
        const currentCount = templateSnap.data().usageCount || 0
        await updateDoc(templateRef, {
          usageCount: currentCount + 1,
          lastUsedAt: serverTimestamp()
        })
      }
    } catch (error) {
      console.error('Error incrementing usage count:', error)
      // Don't throw - this is non-critical
    }
  }

  /**
   * Get default templates (for first-time users)
   */
  getDefaultTemplates() {
    return [
      // COLD OUTREACH
      {
        name: 'Cold Outreach - B2B SaaS',
        category: 'cold_outreach',
        subject: 'Quick question about {{companyName}}',
        body: `Hi {{firstName}},

I noticed {{companyName}} is in the {{industry}} space and wanted to reach out.

We help companies like yours increase their lead generation by 300% through AI-powered automation.

Would you be open to a quick 15-minute call this week to explore how we can help {{companyName}}?

Best regards,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Professional cold outreach for B2B SaaS prospects'
      },
      {
        name: 'Cold Outreach - Problem Agitation',
        category: 'cold_outreach',
        subject: 'Are you still doing {{industry}} lead gen manually?',
        body: `Hi {{firstName}},

I was looking at {{companyName}} and had to reach out.

Most {{industry}} companies waste 20+ hours/week on manual prospecting. That's time you could spend closing deals.

We've built an AI system that automates the entire process - from finding leads to booking meetings.

Worth a quick 10-minute demo?

Best,
[Your Name]

P.S. We helped a similar company in {{industry}} 5x their qualified meetings in 30 days.`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Problem-agitation-solution approach for cold outreach'
      },
      {
        name: 'Cold Outreach - Social Proof',
        category: 'cold_outreach',
        subject: '{{companyName}} + [Your Company] partnership?',
        body: `Hi {{firstName}},

We just helped [Similar Company] in {{industry}} increase their pipeline by $2M in Q1.

I'm reaching out because {{companyName}} has a similar profile, and I think we could deliver comparable results.

Our AI-powered platform handles:
- Lead generation & qualification
- Personalized outreach at scale  
- Meeting scheduling & follow-up

Can I send over a quick case study?

Best,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Social proof-based cold outreach'
      },

      // FOLLOW-UP
      {
        name: 'Follow-up #1 (No Response)',
        category: 'follow_up',
        subject: 'Re: Quick question about {{companyName}}',
        body: `Hi {{firstName}},

I wanted to follow up on my previous email about helping {{companyName}} with lead generation.

I understand you're busy, so I'll keep this brief:
- We've helped similar companies in {{industry}} increase conversions by 3x
- Our AI automation handles the heavy lifting
- Setup takes less than 30 minutes

Would Thursday at 2 PM work for a quick call?

Thanks,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'First follow-up for prospects who haven\'t responded'
      },
      {
        name: 'Follow-up #2 (Value Add)',
        category: 'follow_up',
        subject: 'Thought this might help {{companyName}}',
        body: `Hi {{firstName}},

I haven't heard back, so I figured you're swamped.

I put together a quick resource that's been helping {{industry}} companies like {{companyName}}:

[Link to free guide/tool/checklist]

No strings attached - just thought it might be useful.

If you ever want to chat about scaling your lead gen, I'm here.

Best,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Second follow-up with value-add content'
      },
      {
        name: 'Follow-up #3 (Breakup Email)',
        category: 'follow_up',
        subject: 'Should I close your file?',
        body: `Hi {{firstName}},

I've reached out a few times about helping {{companyName}} with lead generation, but haven't heard back.

I don't want to be a pest, so I'll take the hint and close your file.

If things change and you'd like to explore how we can help {{industry}} companies grow, just hit reply.

All the best,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Final breakup follow-up email'
      },

      // NURTURE
      {
        name: 'Nurture - Educational Content',
        category: 'nurture',
        subject: '5 lead gen mistakes {{industry}} companies make',
        body: `Hi {{firstName}},

I've been working with {{industry}} companies for years, and I keep seeing the same costly mistakes.

Here are the top 5:

1. Buying bad lead lists
2. Generic outreach messages
3. No follow-up system
4. Ignoring lead scoring
5. Manual processes that don't scale

We created a free guide that breaks down each mistake and how to fix it.

Want me to send it over?

Cheers,
[Your Name]`,
        variables: ['firstName', 'industry'],
        description: 'Educational nurture content'
      },
      {
        name: 'Nurture - Case Study Share',
        category: 'nurture',
        subject: 'How [Company] got 300 qualified leads in 30 days',
        body: `Hi {{firstName}},

Quick case study I thought you'd find interesting:

[Company Name] in {{industry}} was struggling with:
- Low lead quality
- High cost per acquisition
- Sales team wasting time on bad leads

We implemented our AI system and in 30 days:
✅ 300 qualified leads generated
✅ 47% reduction in cost per lead
✅ 5x increase in sales meetings

Full breakdown here: [Link]

Think {{companyName}} could benefit from similar results?

Best,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Case study nurture email'
      },
      {
        name: 'Nurture - Re-engagement',
        category: 'nurture',
        subject: 'Still interested in scaling {{companyName}}?',
        body: `Hi {{firstName}},

It's been a while since we last connected about {{companyName}}'s lead generation.

Things have changed a lot on our end - we've rolled out some new features specifically for {{industry}} companies:

• AI-powered lead scoring
• Automated multi-channel outreach
• Built-in CRM with pipeline tracking

If you're still looking to scale your lead gen, I'd love to show you what's new.

10-minute demo this week?

Best,
[Your Name]`,
        variables: ['firstName', 'companyName', 'industry'],
        description: 'Re-engagement email for cold leads'
      },

      // SALES
      {
        name: 'Sales - Demo Follow-up',
        category: 'sales',
        subject: 'Thanks for the demo, {{firstName}}!',
        body: `Hi {{firstName}},

Great chatting with you today about {{companyName}}'s growth goals.

As discussed, here's what we covered:
• Your current lead gen challenges
• How our AI platform solves them
• Expected ROI for {{companyName}}

Next steps:
1. Review the proposal I sent
2. Schedule implementation call
3. Get you live in 48 hours

Any questions before we move forward?

Best,
[Your Name]`,
        variables: ['firstName', 'companyName'],
        description: 'Follow-up after product demo'
      },
      {
        name: 'Sales - Proposal Follow-up',
        category: 'sales',
        subject: 'Thoughts on the {{companyName}} proposal?',
        body: `Hi {{firstName}},

Just checking in on the proposal I sent over for {{companyName}}.

I know you mentioned wanting to get started before Q2, and we have some availability next week to get you onboarded.

Any questions I can answer?

Happy to jump on a quick call if that's easier.

Best,
[Your Name]`,
        variables: ['firstName', 'companyName'],
        description: 'Follow-up on sent proposal'
      },
      {
        name: 'Sales - Objection Handler - Price',
        category: 'sales',
        subject: 'Re: Pricing for {{companyName}}',
        body: `Hi {{firstName}},

I understand the investment is a consideration for {{companyName}}.

Let me break down the ROI:

Current cost: $X per lead (manual process)
Our cost: $Y per lead (automated)

If you're generating 100 leads/month:
- Current monthly cost: $X,XXX
- Our monthly cost: $X,XXX
- Monthly savings: $X,XXX
- Payback period: 2-3 months

Plus, your team gets 20+ hours back every week.

Want to walk through the numbers together?

Best,
[Your Name]`,
        variables: ['firstName', 'companyName'],
        description: 'Handle pricing objections'
      },

      // ONBOARDING
      {
        name: 'Welcome New Lead',
        category: 'onboarding',
        subject: 'Welcome to Market Genie, {{firstName}}! 🎉',
        body: `Hi {{firstName}},

Welcome aboard! We're excited to help {{companyName}} grow.

Here's what to do next:
1. Complete your profile setup
2. Connect your integrations (Hunter.io, Apollo, etc.)
3. Create your first automated workflow

Need help? Our team is here for you.

Let's make great things happen!

Best,
The Market Genie Team`,
        variables: ['firstName', 'companyName'],
        description: 'Onboarding email for new users'
      },
      {
        name: 'Onboarding - Getting Started Guide',
        category: 'onboarding',
        subject: 'Your first week with Market Genie',
        body: `Hi {{firstName}},

Welcome to day 2 with Market Genie!

Here's your getting started checklist:

✅ Day 1: Connect your email & CRM
⏳ Day 2: Import your first 100 contacts
⏳ Day 3: Set up your first email sequence
⏳ Day 4: Configure lead scoring
⏳ Day 5: Launch your first campaign

Need help with any step? Reply to this email or book a 15-min setup call: [Calendar Link]

You've got this!

Best,
[Your Name]`,
        variables: ['firstName'],
        description: 'Week 1 onboarding guidance'
      },
      {
        name: 'Onboarding - Check-in',
        category: 'onboarding',
        subject: 'How\'s your first week going, {{firstName}}?',
        body: `Hi {{firstName}},

Just checking in to see how your first week with Market Genie is going!

Have you been able to:
☐ Connect your tools
☐ Import contacts  
☐ Send your first campaign

If you're stuck on anything, I'm here to help. Just hit reply!

Also, we have weekly training webinars every Thursday at 2 PM - great way to learn pro tips.

Best,
[Your Name]`,
        variables: ['firstName'],
        description: 'Week 1 check-in email'
      }
    ]
  }

  /**
   * Initialize default templates for new user
   */
  async initializeDefaultTemplates(tenantId) {
    try {
      const existingTemplates = await this.getTemplates(tenantId)
      
      if (existingTemplates.length === 0) {
        const defaultTemplates = this.getDefaultTemplates()
        
        const promises = defaultTemplates.map(template => 
          this.createTemplate(tenantId, template)
        )
        
        await Promise.all(promises)
        console.log('✅ Default templates initialized')
      }
    } catch (error) {
      console.error('Error initializing default templates:', error)
      throw error
    }
  }
}

export default new EmailTemplateService()
